import {withRouter} from 'react-router-dom';
import PropTypes from 'prop-types';
import {AbstractFormComponent, EntityIllustration, Icon} from 'front-onceforall-core';
import {requiredValidator} from 'front-onceforall-core/dist/utils/validators';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {I18n, Translate} from 'react-redux-i18n';
import {TextField} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import {parse} from 'query-string';
import {DOCUMENT_STATE, DOSSIER_TYPE} from 'front-onceforall-core/dist/utils/documents';
import DocumentListReadOnlyView from '../documentList/DocumentListReadOnlyView';
import {getDistinctCountries, getDocumentsByCountry, getFirstDossierCountry} from '../../utils/document';
import organisationService from '../../services/organisation/OrganisationService';
import * as snackbarsActions from '../../actions/snackbars';
import documentService from '../../services/document/DocumentService';
import {documentReminderReason} from '../../utils/constant';
import {captureError} from '../../utils/log';
import {getOrganisationLogoUrl} from '../../utils/organisation';

export class RequestSpecificDocuments extends AbstractFormComponent {
  constructor(props) {
    super(props);
    const search = parse(props.location.search);
    this.state = {...this.state,
      documents: [],
      currentDocuments: [],
      subcontractor: null,
      countries: [],
      dossierCountry: search.country ? search.country : null};

    this.handleCheckEvent = this.handleCheckEvent.bind(this);
    this.getFormDataDocuments = this.getFormDataDocuments.bind(this);
    this.fetchDocuments = this.fetchDocuments.bind(this);
    this.buildSelectCountries = this.buildSelectCountries.bind(this);
    this.handleDossierCountryChange = this.handleDossierCountryChange.bind(this);
    this.getFormData = this.getFormData.bind(this);
  }

  componentDidMount() {
    const subcontractorId = Number(this.props.match.params.organisationId);

    // Fetch subcontractor data and subcontractor documents
    organisationService.get(subcontractorId)
      .then(subcontractor => {
        this.setState({subcontractor});
        this.fetchDocuments(subcontractorId);
      });
  }

  fetchDocuments(subcontractorId) {
    const {interventionId} = this.getFormData();
    const isInProjectContext = interventionId !== null;
    const isSelf = subcontractorId === this.props.organisation.organisationId;
    let documentPromise;

    if (isInProjectContext) {
      documentPromise = isSelf ?
        documentService.searchByProject(interventionId) :
        documentService.searchByProjectOfSubcontractor(interventionId, subcontractorId, this.props.organisation.organisationId);
    } else {
      documentPromise = isSelf ?
        documentService.search(this.props.organisation.organisationId) :
        documentService.searchOfSubcontractor(subcontractorId, this.props.organisation.organisationId);
    }

    documentPromise.then(documents => {
      const countries = getDistinctCountries(documents);
      let {dossierCountry} = this.state;
      if (!this.state.dossierCountry || !countries.includes(dossierCountry)) {
        dossierCountry = getFirstDossierCountry(countries, this.props.organisation.country, this.state.subcontractor.country);
      }

      const currentDocuments = getDocumentsByCountry(documents, dossierCountry);
      const filteredDocuments = this.getFilteredDocuments(currentDocuments);

      this.setState({
        documents,
        currentDocuments: filteredDocuments,
        countries,
        dossierCountry,
        loading: false,
        formData: this.getFormDataDocuments(filteredDocuments)
      });
    }).catch(error => {
      captureError('documentPromise.then', error);
      this.setState({
        documents: [],
        currentDocuments: [],
        countries: [],
        loading: false
      });
      this.props.actions.addError(<Translate value="error.generic"/>);
    });
  }

  isInvalid(document) {
    return [
      DOCUMENT_STATE.CREATED,
      DOCUMENT_STATE.NOT_APPLICABLE,
      DOCUMENT_STATE.AWAITING_GATHERING,
      DOCUMENT_STATE.AWAITING_COMPLETION,
      DOCUMENT_STATE.MISSING,
      DOCUMENT_STATE.REJECTED,
      DOCUMENT_STATE.AWAITING_SIGNATURE
    ].includes(document.state);
  }

  getFilteredDocuments(documents) {
    return documents.filter(document => document.state !== DOCUMENT_STATE.NOT_APPLICABLE);
  }

  getFormDataDocuments(documents) {
    const {formData} = this.state;
    formData.documentsSelected = [];
    documents.forEach(document => {
      formData.documentsSelected[document.uid] = Boolean(
        document.type.dossier === DOSSIER_TYPE.LEGAL && this.isInvalid(document)
      );
    });
    return formData;
  }

  getFormData() {
    const search = parse(this.props.location.search);
    return {
      projectId: search.projectId ? search.projectId : '',
      projectName: search.projectName ? search.projectName : '',
      projectNameDisabled: Boolean(search.projectName),
      interventionId: search.interventionId ? search.interventionId : null,
      reason: '',
      additionalContext: '',
      documentsSelected: []
    };
  }

  getFormValidators() {
    return {
      projectName: requiredValidator,
      reason: requiredValidator,
      additionalContext: null,
      documentsSelected: values => {
        if (Object.keys(values).every(key => !values[key])) {
          return 'document.remind.documents.error';
        }

        return null;
      }
    };
  }

  handleCheckEvent(documentUID) {
    const formData = {...this.state.formData};
    formData.documentsSelected[documentUID] = !formData.documentsSelected[documentUID];
    this.setState({formData}, this.state.submitted ? () => this.validateField('documentsSelected', formData.documentsSelected) : null);
  }

  handleSubmitCallback() {
    const {formData} = this.state;
    const documentIds = this.state.currentDocuments
      .filter(document => formData.documentsSelected[document.uid])
      .map(document => document.uid);

    const data = {
      remindingOrganisationId: this.props.organisation.organisationId,
      remindedOrganisationId: Number(this.props.match.params.organisationId),
      projectId: formData.projectId,
      projectName: formData.projectName,
      reason: formData.reason,
      additionalContext: formData.additionalContext,
      documentIds
    };

    documentService.createDocumentRemindersRequest(data)
      .then(() => {
        this.props.actions.addSuccess(<Translate value="document.remind.success"/>);
        this.props.history.goBack();
      })
      .catch(() => {
        this.setState({loading: false, submitting: false});
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  handleDossierCountryChange(event) {
    const dossierCountry = event.target.value;
    const currentDocuments = getDocumentsByCountry(this.state.documents, dossierCountry);
    const documentsToRequest = this.getFilteredDocuments(currentDocuments);

    this.setState({
      dossierCountry,
      currentDocuments: documentsToRequest,
      formData: this.getFormDataDocuments(documentsToRequest)
    });
  }

  buildSelectCountries() {
    let selectedCountry = '';
    if (this.state.countries.length > 0) {
      selectedCountry = this.state.countries.includes(
        this.state.dossierCountry
      ) ? this.state.dossierCountry : this.state.countries[0];
    }

    const countryItems = this.state.countries.map(country => <MenuItem
      key={`country-${country}`}
      value={country}
    ><Translate value={`country.${country}`}/></MenuItem>);

    return (<TextField
      data-id="dossier-country-filter"
      select
      value={selectedCountry}
      onChange={this.handleDossierCountryChange}
    >
      {countryItems}
    </TextField>);
  }

  getSubcontractorDetails(subcontractor) {
    return (
      <>
        <div className="col-md-6 inline-container">
          <EntityIllustration
            country={subcontractor.country}
            name={subcontractor.fullName}
            imageUrl={getOrganisationLogoUrl(subcontractor.organisationId)}
          />
          <div className="inline-container column ml-3">
            <strong style={{fontSize: '2.5rem'}}>{subcontractor.fullName}</strong>
            <span>{subcontractor.registrationNumber}</span>
          </div>
        </div>

        <div className="col-md-6 inline-container column information">
          <div>{subcontractor.address.addressLine}</div>
          <div>
            <span>{subcontractor.address.postCode} {subcontractor.address.city} </span>
            <Translate value={`country.${subcontractor.country}`}/>
          </div>
        </div>
      </>
    );
  }

  getContextForm() {
    return (<>
      <div className="row">
        <div className="col-md-4 d-md-flex justify-content-start align-items-center">
          <Translate value="document.remind.subMessageProject"/>
        </div>
        <div className="col-md-8">
          <TextField
            label={this.getFieldLabel('project.name', true)}
            value={this.state.formData.projectName}
            onChange={this.handleChange('projectName')}
            error={this.isOnError('projectName')}
            helperText={this.helperText('projectName')}
            disabled={this.state.formData.projectNameDisabled}
          />
        </div>
        <div className="col-md-4 d-md-flex justify-content-start align-items-center">
          <Translate value="document.remind.subMessageReason"/>
        </div>
        <div className="col-md-8">
          <TextField
            select
            label={this.getFieldLabel('document.remind.reason.name', true)}
            value={this.state.formData.reason}
            onChange={this.handleChange('reason')}
            error={this.isOnError('reason')}
            helperText={this.helperText('reason')}
          >
            {Object.values(documentReminderReason).map((key, index) => <MenuItem
              key={index}
              value={key}
            >
              <Translate value={`document.remind.reason.${key}`}/>
            </MenuItem>)}
          </TextField>
        </div>
      </div>
      <TextField
        multiline
        rows={3}
        label={this.getFieldLabel('document.remind.additionalContext')}
        value={this.state.formData.additionalContext}
        onChange={this.handleChange('additionalContext')}
        error={this.isOnError('additionalContext')}
        helperText={this.helperText('additionalContext')}
      />
    </>
    );
  }

  getDocumentsForm() {
    return (
      <div style={{paddingLeft: 25}}>
        <DocumentListReadOnlyView
          documents={this.state.currentDocuments}
          documentsSelected={this.state.formData.documentsSelected}
          onCheckEvent={this.handleCheckEvent}
        />
        {this.isOnError('documentsSelected') &&
        <span className="error mt-4"><Translate value="document.remind.documents.error"/></span>
        }
      </div>
    );
  }

  render() {
    const submitButton = (
      <button
        type="submit"
        className="large primary inline-container"
        disabled={this.isFormInvalid()}
      >
        <Icon icon={this.state.submitting ? 'loader' : 'check'}/>
        <Translate value="action.confirm"/>
      </button>);
    return (
      <>
        <div className="mt-4 mb-5">
          <h1 className="m-0"><Translate value="document.remind.title"/></h1>
        </div>

        <form
          className="card"
          onSubmit={this.handleSubmit}
        >
          {this.state.loading && <div className="loading-overlay"/>}

          <div className="row">
            {!this.state.subcontractor && <Icon icon="loader"/>}
            {this.state.subcontractor && this.getSubcontractorDetails(this.state.subcontractor)}
          </div>

          <p className="pl-4 py-4 information">
            <Translate
              value="document.remind.information"
              dangerousHTML
            />
          </p>

          <h2><Translate value="document.remind.context"/></h2>
          {this.getContextForm()}

          <div className="row mt-5">
            <div className="col-lg-10">
              <h2><Translate value="document.remind.documents.title"/></h2>
            </div>
            <div className="col-lg-2">
              {this.state.countries.length > 1 && this.buildSelectCountries()}
            </div>
          </div>
          {this.getDocumentsForm()}

          <div className="actions col-12 inline-container space-between">
            <button
              type="button"
              className="large secondary inline-container"
              onClick={() => this.props.history.goBack()}
            >
              <Icon icon="go-back"/>
              <Translate value="action.cancel"/>
            </button>
            {this.state.fieldErrors.documentsSelected === 'document.remind.documents.error' ?
              <span
                tooltip={I18n.t('document.remind.documents.error')}
                flow="left"
              >{submitButton}</span> :
              submitButton
            }
          </div>
        </form>
      </>
    );
  }
}

RequestSpecificDocuments.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      organisationId: PropTypes.string.isRequired
    })
  }),
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  history: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RequestSpecificDocuments));
